import { Box, Divider, Paper, Typography } from '@mui/material';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';
import './StyleSettings.css';
import { selectLogoIdDark, selectLogoIdLight } from '@store/reducer/companyReducer';

import ThemeEditorCard from '../../_UnderConstruction/ThemeEditor/ThemeEditorCard';

import LogoUploadCard from './LogoUploadCard';

/**
 * StyleSettings component.
 * Handles the company style settings, including colors and logo.
 *
 * @returns {JSX.Element} The JSX element representing the StyleSettings.
 */
function StyleSettings(): JSX.Element {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).settings;

    return (
        <Paper sx={{ mt: 3, p: 3, bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    {langStrings.designTitle}
                </Typography>
                <Divider />
                <ThemeEditorCard />
                <ImageSelectorBox />
            </Box>
        </Paper>
    );
}

function ImageSelectorBox() {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).settings;
    const logoIdLight = useSelector(selectLogoIdLight);
    const logoIdDark = useSelector(selectLogoIdDark);

    const [loadingText, setLoadingText] = React.useState<string | null>(null);
    const [loadingError, setLoadingError] = React.useState<string | null>(null);

    React.useEffect(() => {
        setLoadingError(null);
        setLoadingText(null);
    }, [logoIdLight, logoIdDark]);

    const handleUploadStart = (text: string) => {
        setLoadingText(text);
        setLoadingError(null);
    };

    const handleUploadError = (error: string) => {
        setLoadingError(error);
        setLoadingText(null);
    };

    const handleUploadComplete = () => {
        setLoadingText(null);
    };

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {langStrings.companyLogoTitle}
            </Typography>

            <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
                <LogoUploadCard
                    mode="light"
                    logoId={logoIdLight}
                    onUploadStart={handleUploadStart}
                    onUploadError={handleUploadError}
                    onUploadComplete={handleUploadComplete}
                    disabled={loadingText !== null}
                />
                <LogoUploadCard
                    mode="dark"
                    logoId={logoIdDark}
                    onUploadStart={handleUploadStart}
                    onUploadError={handleUploadError}
                    onUploadComplete={handleUploadComplete}
                    disabled={loadingText !== null}
                />
            </Box>

            {loadingText && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                    {loadingText}
                </Typography>
            )}
            {loadingError && (
                <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                    {loadingError}
                </Typography>
            )}
        </Box>
    );
}

export default StyleSettings;
